import { useEffect, useRef, useState as useStateRaw, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';
import { useLanguageContext } from '@/i18n/LanguageContext';
import Seo from '@/components/seo/Seo';
import HeroSection from './components/HeroSection';
import JobDefinition from './components/JobDefinition';
import WorkDomains from './components/WorkDomains';
import DayFlow from './components/DayFlow';
import Workplaces from './components/Workplaces';
import IncomeSection from './components/IncomeSection';
import Qualities from './components/Qualities';
import Fitness from './components/Fitness';
import PathToCareer from './components/PathToCareer';
import FAQSection, { faqKeys } from './components/FAQSection';
import CTASection from './components/CTASection';
import RelatedPages from './components/RelatedPages';

function FadeInSection({ children, className = '' }: { children: ReactNode; className?: string }) {
  const ref = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useStateRaw(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.unobserve(el);
        }
      },
      { threshold: 0.1 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <div ref={ref} className={`fade-in-section ${isVisible ? 'is-visible' : ''} ${className}`}>
      {children}
    </div>
  );
}

function renderParagraphs(text: string, className: string) {
  return text.split(/\n{2,}/).map((paragraph, index) => (
    <p key={index} className={className}>
      {paragraph}
    </p>
  ));
}

export default function ButlerJobPage() {
  const { lang } = useLanguageContext();
  const { t } = useTranslation();

  const breadcrumbStructuredData = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: lang === 'en' ? 'Home' : 'ホーム',
        item: 'https://butler-academy.jp/',
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: t('butlerJob.hero.currentPage').replace(/\n/g, ''),
        item: 'https://butler-academy.jp/butler-job',
      },
    ],
  };

  const faqStructuredData = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqKeys.map((faq) => ({
      '@type': 'Question',
      name: t(faq.q).replace(/\n/g, ''),
      acceptedAnswer: {
        '@type': 'Answer',
        text: t(faq.a).replace(/\n/g, ''),
      },
    })),
  };

  return (
    <>
      <Header />
      <main>
        <Seo route="/butler-job" />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbStructuredData) }}
        />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(faqStructuredData) }}
        />

        <HeroSection />

        <FadeInSection>
          <JobDefinition />
        </FadeInSection>

        <FadeInSection>
          <WorkDomains />
        </FadeInSection>

        <FadeInSection>
          <DayFlow />
        </FadeInSection>

        <FadeInSection>
          <Workplaces />
        </FadeInSection>

        <FadeInSection>
          <IncomeSection />
        </FadeInSection>

        <FadeInSection>
          <Qualities />
        </FadeInSection>

        <FadeInSection>
          <Fitness />
        </FadeInSection>

        <FadeInSection>
          <PathToCareer />
        </FadeInSection>

        <FadeInSection>
          <FAQSection />
        </FadeInSection>

        <FadeInSection>
          <CTASection />
        </FadeInSection>

        <FadeInSection>
          <RelatedPages />
        </FadeInSection>

        <div className="bg-background-50 py-8">
          {renderParagraphs(
            t('butlerJob.lastUpdated'),
            'text-center text-[13px] text-foreground-400 tracking-[0.05em]'
          )}
        </div>

        <button
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          className="fixed bottom-8 right-8 w-11 h-11 rounded-full border border-accent-500/40 text-accent-500 hover:bg-accent-500/10 hover:border-accent-400 transition-all duration-300 flex items-center justify-center cursor-pointer z-40"
          aria-label={t('common.backToTop')}
        >
          <i className="ri-arrow-up-line"></i>
        </button>
      </main>
      <Footer />
    </>
  );
}
